import type { Store as EffectorStore } from 'effector'

export const MediaOrderType = {
  FIRST: 'FIRST',
  DEFAULT: 'DEFAULT',
  LAST: 'LAST',
} as const
export type MediaOrderType =
  (typeof MediaOrderType)[keyof typeof MediaOrderType]

export const MediaResolution = {
  HD: 'HD',
  SD: 'SD',
  FHD: 'FHD',
  _2K: '_2K',
  _4K: '_4K',
} as const
export type MediaResolution =
  (typeof MediaResolution)[keyof typeof MediaResolution]

export interface GenericApiResponse<T> {
  status: 'OK' | 'ERROR'
  errorCode?: string
  message?: string
  payload: T
}

const Providers = {
  EZDRM: 'EZDRM',
  PALLYCON: 'PALLYCON',
  EXPRESS_PLAY: 'EXPRESS_PLAY',
} as const
type Providers = (typeof Providers)[keyof typeof Providers]

export interface LaData {
  licenseUrl?: string
  headers?: Record<string, string>
  fpsCertificateUrl?: string
  provider?: Providers
  iv?: string
  contentId?: string
  persistentLicense?: boolean
}

export interface DRM {
  la: Record<string, LaData>
}

export type SkipSegmentType = 'INTRO' | 'CREDITS'

export interface SkipSegment {
  showButtonTime: number // ms
  hideButtonTime: number // ms
  segmentEndTime: number // ms
  type: SkipSegmentType
}

export interface MediaUrl {
  playbackUrl: string
  /** Debug-only playback request headers (e.g. from DevTools /url override). */
  headers?: Record<string, string>
  drm?: DRM | null
  stoppedTime?: number
  skipSegments?: SkipSegment[]
  liveRewindOptions?: {
    dvr: boolean
    rewind: boolean
    timeshift: boolean
    rewindLengthSec?: number | null | undefined
    storageDurationSec?: number | null | undefined
  }
}

export const VideoType = {
  VOD: 'VOD',
  VOD_TRAILER: 'VOD_TRAILER',
  SHOW: 'SHOW',
  SHOW_TRAILER: 'SHOW_TRAILER',
  CHANNEL: 'CHANNEL',
  EVENT: 'EVENT',
} as const
export type VideoType = (typeof VideoType)[keyof typeof VideoType]

export const MediaTypes = {
  TV_CHANNEL: 'TV_CHANNEL',
  TV_SHOW: 'TV_SHOW',
  VOD: 'VOD',
  EPISODE: 'EPISODE',
  TV_CATEGORY: 'TV_CATEGORY',
  TV_SUBCATEGORY: 'TV_SUBCATEGORY',
  VOD_CATEGORY: 'VOD_CATEGORY',
  VOD_SUBCATEGORY: 'VOD_SUBCATEGORY',
  TV_SHOW_CATEGORY: 'TV_SHOW_CATEGORY',
  TV_SHOW_SUBCATEGORY: 'TV_SHOW_SUBCATEGORY',
  LIVE_EVENT: 'LIVE_EVENT', // ContentType != status. LIVE_EVENT contentType includes all events with statuses LIVE, SOON, LIVE_SOON, ENDED
  RADIO_CHANNEL: 'RADIO_CHANNEL',
  PARTNER_PRODUCT: 'PARTNER_PRODUCT',
  MOTN_TV_SHOW: 'MOTN_TV_SHOW',
  MOTN_VOD: 'MOTN_VOD',
} as const
export type MediaTypes = (typeof MediaTypes)[keyof typeof MediaTypes]

export const GlobalSearchMediaTypes = {
  LIVE_EVENT: MediaTypes.LIVE_EVENT,
  TV_CHANNEL: MediaTypes.TV_CHANNEL,
  TV_SHOW: MediaTypes.TV_SHOW,
  VOD: MediaTypes.VOD,
} as const
export type GlobalSearchMediaTypes =
  (typeof GlobalSearchMediaTypes)[keyof typeof GlobalSearchMediaTypes]

export const LiveEventsTypes = {
  LIVE: 'LIVE',
  UPCOMING: 'UPCOMING',
  PAST: 'PAST',
} as const
export type LiveEventsTypes =
  (typeof LiveEventsTypes)[keyof typeof LiveEventsTypes]

export const ResourceTypes = {
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'EXTERNAL',
  WITHOUT_LINK: 'WITHOUT_LINK',
} as const
export type ResourceTypes = (typeof ResourceTypes)[keyof typeof ResourceTypes]

export type WrappedData<T> = { content: T }

export type ContentStore<T> = EffectorStore<WrappedData<T>>
